import {
  BankOutlined,
  CloseOutlined,
  FileTextOutlined,
  HomeOutlined,
  MailOutlined,
  PhoneOutlined,
  SaveOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Alert, Button, Col, Divider, Form, Input, message, Row, Select, Space } from 'antd';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../config/api';

const { TextArea } = Input;
const { Option } = Select;

interface Organization {
  id: string;
  name: string;
}

interface CompanyFormProps {
  company?: any;
  onSuccess: () => void;
  onCancel: () => void;
}

const CompanyForm: React.FC<CompanyFormProps> = ({ company, onSuccess, onCancel }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [organizations, setOrganizations] = useState<Organization[]>([]);

  useEffect(() => {
    fetchOrganizations();
    if (company) {
      form.setFieldsValue({
        ...company,
        organizationId: company.organizationId || company.organization?.id,
      });
    }
  }, [company, form]);

  const fetchOrganizations = async () => {
    try {
      const response = await api.get('/organizations');
      setOrganizations(response.data);
    } catch (error) {
      message.error(t('companyForm.orgsLoadFailed'));
    }
  };

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      if (company) {
        // Remove organizationId from update data as it's not allowed to be changed
        const { organizationId, ...updateData } = values;
        await api.patch(`/companies/${company.id}`, updateData);
        message.success(t('companyForm.updateSuccess'));
      } else {
        await api.post('/companies', values);
        message.success(t('companyForm.createSuccess'));
      }
      onSuccess();
      form.resetFields();
    } catch (error: any) {
      message.error(error.response?.data?.message || t('companyForm.saveFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={{
        organizationId: organizations[0]?.id,
      }}
    >
      <Alert
        message={t('companyForm.infoTitle')}
        description={t('companyForm.infoBody')}
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Divider orientation="left">
        <BankOutlined /> {t('companyForm.sectionBasic')}
      </Divider>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="name"
            label={t('companyForm.name')}
            rules={[{ required: true, message: t('companyForm.nameRequired') }]}
          >
            <Input
              prefix={<BankOutlined />}
              placeholder={t('companyForm.namePlaceholder')}
              size="large"
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="taxNumber"
            label={t('companyForm.taxNumber')}
            rules={[
              {
                pattern: /^[A-Z0-9-]+$/i,
                message: t('companyForm.taxNumberInvalid'),
              },
            ]}
          >
            <Input placeholder={t('companyForm.taxNumberPlaceholder')} size="large" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="organizationId"
            label={t('companyForm.organization')}
            rules={[{ required: true, message: t('companyForm.organizationRequired') }]}
          >
            <Select
              placeholder={t('companyForm.organizationPlaceholder')}
              size="large"
              showSearch
              disabled={!!company}
              filterOption={(input, option) =>
                (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
              }
            >
              {organizations.map((org) => (
                <Option key={org.id} value={org.id}>
                  {org.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="address" label={t('companyForm.address')}>
            <Input
              prefix={<HomeOutlined />}
              placeholder={t('companyForm.addressPlaceholder')}
              size="large"
            />
          </Form.Item>
        </Col>
      </Row>

      <Divider orientation="left">
        <UserOutlined /> {t('companyForm.sectionContact')}
      </Divider>

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item name="contactPerson" label={t('companyForm.contactPerson')}>
            <Input
              prefix={<UserOutlined />}
              placeholder={t('companyForm.contactPersonPlaceholder')}
              size="large"
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="contactEmail"
            label={t('companyForm.contactEmail')}
            rules={[
              { required: true, message: t('companyForm.contactEmailRequired') },
              { type: 'email', message: t('companyForm.contactEmailInvalid') },
            ]}
          >
            <Input prefix={<MailOutlined />} placeholder="contact@company.com" size="large" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="contactPhone"
            label={t('companyForm.contactPhone')}
            rules={[
              {
                pattern: /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/,
                message: t('companyForm.contactPhoneInvalid'),
              },
            ]}
          >
            <Input prefix={<PhoneOutlined />} placeholder="+36 1 234 5678" size="large" />
          </Form.Item>
        </Col>
      </Row>

      <Divider orientation="left">
        <FileTextOutlined /> {t('companyForm.sectionBilling')}
      </Divider>

      <Row gutter={16}>
        <Col span={24}>
          <Form.Item
            name="billingAddress"
            label={t('companyForm.billingAddress')}
            tooltip={t('companyForm.billingAddressTooltip')}
          >
            <TextArea placeholder={t('companyForm.billingAddressPlaceholder')} rows={2} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={24}>
          <Form.Item
            name="notes"
            label={t('companyForm.notes')}
            tooltip={t('companyForm.notesTooltip')}
          >
            <TextArea placeholder={t('companyForm.notesPlaceholder')} rows={3} />
          </Form.Item>
        </Col>
      </Row>

      <Divider />

      <Form.Item>
        <Space>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            icon={<SaveOutlined />}
            size="large"
          >
            {company ? t('companyForm.update') : t('companyForm.create')}
          </Button>
          <Button onClick={onCancel} icon={<CloseOutlined />} size="large">
            {t('companyForm.cancel')}
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
};

export default CompanyForm;
